import { uiText, useUiLanguage, currentLocale } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, CheckCircle, XCircle, Video, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';

interface TrainingSession {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  max_participants: number | null;
  age_group: string;
  session_type?: string;
  online_meeting_url?: string | null;
  meeting_platform?: string | null;
  meeting_id?: string | null;
  meeting_password?: string | null;
  attendance_count?: number;
  user_response?: boolean | null;
}

interface WeeklyTrainingPlanProps {
  sessions?: TrainingSession[];
}

const WeeklyTrainingPlan: React.FC<WeeklyTrainingPlanProps> = ({ sessions: propSessions }) => {
  useUiLanguage();
  const { profile } = useAuth();
  const { t } = useTranslation(['common', 'dashboard']);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  const fetchWeeklyTrainingSessions = async () => {
    if (!profile?.dojo_id) return;

    setIsLoading(true);
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(currentWeekStart.getDate() + 6);

      // Get sessions filtered by student's age group
      let query = supabase
        .from('training_sessions')
        .select(`
          id,
          title,
          date,
          start_time,
          end_time,
          description,
          max_participants,
          age_group,
          session_type,
          online_meeting_url,
          meeting_platform,
          meeting_id,
          meeting_password,
          status
        `)
        .eq('dojo_id', profile.dojo_id)
        .gte('date', currentWeekStart.toISOString().split('T')[0])
        .lte('date', weekEnd.toISOString().split('T')[0])
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });

      // Filter by age group if specified
      if (profile?.age_group && profile.age_group !== 'all') {
        query = query.or(`age_group.eq.all,age_group.eq.${profile.age_group}`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching weekly training sessions:', error);
        toast({
          title: t('common:messages.error'),
          description: uiText("Failed to load weekly training sessions"),
          variant: "destructive"
        });
        setSessions([]);
        return;
      }

      // Filter out cancelled sessions and only show scheduled/active ones
      const activeSessions = (data || []).filter(session =>
        !session.status || session.status === 'scheduled'
      );

      // Fetch user responses for these sessions
      if (profile?.id && activeSessions.length > 0) {
        const sessionIds = activeSessions.map(s => s.id);
        const { data: attendanceData } = await supabase
          .from('session_attendance')
          .select('session_id, attended')
          .eq('student_id', profile.id)
          .in('session_id', sessionIds);

        if (attendanceData) {
          const responseMap = new Map();
          attendanceData.forEach(att => {
            responseMap.set(att.session_id, att.attended);
          });

          const sessionsWithResponses = activeSessions.map(session => ({
            ...session,
            user_response: responseMap.get(session.id) || null
          }));

          setSessions(sessionsWithResponses);
        } else {
          setSessions(activeSessions);
        }
      } else {
        setSessions(activeSessions);
      }
    } catch (error) {
      console.error('Error fetching weekly training sessions:', error);
      toast({
        title: t('common:messages.error'),
        description: uiText("Failed to load weekly training sessions"),
        variant: "destructive"
      });
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAttendanceResponse = async (sessionId: string, willAttend: boolean) => {
    if (!profile) return;

    try {
      const { data: existingResponse } = await supabase
        .from('session_attendance')
        .select('id')
        .eq('session_id', sessionId)
        .eq('student_id', profile.id)
        .maybeSingle();

      if (existingResponse) {
        const { error } = await supabase
          .from('session_attendance')
          .update({ attended: willAttend })
          .eq('id', existingResponse.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('session_attendance')
          .insert({
            session_id: sessionId,
            student_id: profile.id,
            attended: willAttend
          });

        if (error) throw error;
      }

      // Update local state
      setSessions(prev => prev.map(session =>
        session.id === sessionId
          ? { ...session, user_response: willAttend }
          : session
      ));

      toast({
        title: uiText("Response Recorded"),
        description: uiText("Your attendance response has been recorded: {{value0}}", { value0: willAttend ? uiText("Will attend") : uiText("Will not attend") })
      });
    } catch (error) {
      console.error('Error recording attendance response:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to record your response. Please try again."),
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    // Use prop sessions if provided, otherwise fetch from database
    if (propSessions && Array.isArray(propSessions)) {
      // Filter prop sessions for current week
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(currentWeekStart.getDate() + 6);

      const weekSessions = propSessions.filter(session => {
        const sessionDate = new Date(session.date);
        return sessionDate >= currentWeekStart && sessionDate <= weekEnd;
      });

      setSessions(weekSessions);
      setIsLoading(false);
    } else {
      fetchWeeklyTrainingSessions();
    }
  }, [currentWeekStart, profile?.dojo_id, profile?.age_group, propSessions]);

  const formatTime = (timeString: string) => {
    return new Date(`1970-01-01T${timeString}`).toLocaleTimeString(currentLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLocale(), {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAgeGroupBadge = (ageGroup: string) => {
    if (!ageGroup || ageGroup === 'all') return null;

    const getAgeGroupLabel = (group: string) => {
      switch (group) {
        case 'kids':
          return uiText("Kids (5-12)");
        case 'teens':
          return uiText("Teens (13-17)");
        case 'adults':
          return uiText("Adults (18+)");
        default:
          return group;
      }
    };

    const getAgeGroupColor = (group: string) => {
      switch (group) {
        case 'kids':
          return 'border-blue-500 text-blue-700 bg-blue-50';
        case 'teens':
          return 'border-purple-500 text-purple-700 bg-purple-50';
        case 'adults':
          return 'border-green-500 text-green-700 bg-green-50';
        default:
          return 'border-gray-500 text-gray-700 bg-gray-50';
      }
    };

    return (
      <Badge
        variant="outline"
        className={getAgeGroupColor(ageGroup)}
      >
        {getAgeGroupLabel(ageGroup)}
      </Badge>
    );
  };

  const getSessionTypeBadge = (sessionType: string) => {
    const isOnline = sessionType === 'online';
    return (
      <Badge
        variant="outline"
        className={isOnline
          ? 'border-blue-500 text-blue-700 bg-blue-50'
          : 'border-gray-500 text-gray-700 bg-gray-50'
        }
      >
        {isOnline ? (
          <>
            <Video className="h-3 w-3 mr-1" />
            {uiText("Online Session")}</>
        ) : (
          <>
            <MapPin className="h-3 w-3 mr-1" />
            {uiText("In-Person")}</>
        )}
      </Badge>
    );
  };

  const isSessionJoinable = (session: TrainingSession) => {
    if (session.session_type !== 'online' || !session.online_meeting_url) return false;

    const now = new Date();
    const sessionDate = new Date(session.date);
    const [startHour, startMinute] = session.start_time.split(':').map(Number);
    const [endHour, endMinute] = session.end_time.split(':').map(Number);

    const sessionStart = new Date(sessionDate);
    sessionStart.setHours(startHour, startMinute, 0, 0);

    const sessionEnd = new Date(sessionDate);
    sessionEnd.setHours(endHour, endMinute, 0, 0);

    // Allow joining 15 minutes before and during the session
    const joinableStart = new Date(sessionStart.getTime() - 15 * 60 * 1000);

    return now >= joinableStart && now <= sessionEnd;
  };

  const handleJoinMeeting = (session: TrainingSession) => {
    if (session.online_meeting_url) {
      window.open(session.online_meeting_url, '_blank');

      toast({
        title: uiText("Joining Meeting"),
        description: uiText("Opening {{value0}} meeting for {{value1}}", { value0: session.meeting_platform || 'online', value1: session.title })
      });
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newWeekStart);
  };

  const isCurrentWeek = () => {
    const today = new Date();
    const todayWeekStart = new Date(today);
    const dayOfWeek = today.getDay();
    todayWeekStart.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    todayWeekStart.setHours(0, 0, 0, 0);

    return currentWeekStart.getTime() === todayWeekStart.getTime();
  };

  const weekEndDate = new Date(currentWeekStart);
  weekEndDate.setDate(currentWeekStart.getDate() + 6);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-900 mx-auto mb-4"></div>
            <p>{uiText("Loading weekly training plan...")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {uiText("Weekly Training Plan")}</CardTitle>
            <CardDescription>
              {currentWeekStart.toLocaleDateString(currentLocale(), {
                month: 'long',
                day: 'numeric'
              })} - {weekEndDate.toLocaleDateString(currentLocale(), {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
              {isCurrentWeek() && <span className="ml-2 text-green-600 font-medium">{uiText("(This Week)")}</span>}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateWeek('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigateWeek('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!sessions || sessions.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">{uiText("No Training Sessions This Week")}</h3>
            <p className="text-gray-500">
              {uiText("Check back later or contact your instructor for the training schedule.")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map((session) => (
              <div key={session.id} className="p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{session.title}</h4>
                      {getAgeGroupBadge(session.age_group)}
                      {getSessionTypeBadge(session.session_type || 'in_person')}
                    </div>

                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(session.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>
                          {formatTime(session.start_time)} - {formatTime(session.end_time)}
                        </span>
                      </div>
                      {session.max_participants && (
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          <span>
                            {session.attendance_count || 0}/{session.max_participants} {' '}{uiText("registered")}</span>
                        </div>
                      )}
                      {session.session_type === 'online' && session.meeting_platform && (
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4" />
                          <span>{uiText("Platform:")}{' '}{session.meeting_platform}</span>
                        </div>
                      )}
                      {session.session_type === 'online' && session.meeting_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium">{uiText("Meeting ID:")}</span>
                          <span className="font-mono bg-gray-100 px-2 py-1 rounded">{session.meeting_id}</span>
                        </div>
                      )}
                      {session.session_type === 'online' && session.meeting_password && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium">{uiText("Password:")}</span>
                          <span className="font-mono bg-gray-100 px-2 py-1 rounded">{session.meeting_password}</span>
                        </div>
                      )}
                      {session.description && (
                        <p className="mt-2 text-gray-700">{session.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Attendance Confirmation */}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    {session.user_response !== null && (
                      <div className="flex items-center gap-2 mb-2 sm:mb-0">
                        {session.user_response ? (
                          <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {uiText("Will Attend")}</Badge>
                        ) : (
                          <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300">
                            <XCircle className="h-3 w-3 mr-1" />
                            {uiText("Won't Attend")}</Badge>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      {/* Online Session Join Button */}
                      {session.session_type === 'online' && session.online_meeting_url && isSessionJoinable(session) && (
                        <Button
                          size="sm"
                          onClick={() => handleJoinMeeting(session)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Video className="h-3 w-3 mr-1" />
                          {uiText("Join Meeting")}</Button>
                      )}

                      {/* Regular Attendance Buttons */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAttendanceResponse(session.id, false)}
                        className={`${session.user_response === false ? 'border-red-500 text-red-700 bg-red-50' : ''}`}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        {uiText("Can't Attend")}</Button>
                      <Button
                        size="sm"
                        onClick={() => handleAttendanceResponse(session.id, true)}
                        className={`${session.user_response === true ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {uiText("Will Attend")}</Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyTrainingPlan;